/**
 * Kategori-sync-oversikt for systemadmin.
 *
 * Viser:
 *   - Status på daglig sync fra kompetanseheving.no
 *   - Aktive og deprekerte kategorier
 *   - Manuell "Synkroniser nå"-knapp
 *   - Historikk-tabell (siste 30 kjøringer)
 *   - Impact-analyse per deprekert kategori (hvilke kurs bruker den?)
 */

import { useEffect, useState } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import {
  RefreshCw, CheckCircle2, AlertCircle, Clock, Tag, ExternalLink,
  TrendingUp, History, AlertTriangle,
} from 'lucide-react';

interface Category {
  slug: string;
  name: string;
  description: string | null;
  parent_slug: string | null;
  aliases: string[];
  is_active: boolean;
  deprecated_at: string | null;
  replacement_slug: string | null;
  last_synced_at: string;
}

interface SyncLog {
  id: number;
  synced_at: string;
  outcome: 'success' | 'error' | 'partial' | 'no_change';
  source_version: string | null;
  added_slugs: string[];
  renamed: Array<{ slug: string; old_name: string; new_name: string }>;
  deprecated_slugs: string[];
  reactivated_slugs: string[];
  updated_slugs: string[];
  error_message: string | null;
  categories_before: number;
  categories_after: number;
  duration_ms: number;
  triggered_by: string;
}

interface Overview {
  source_url: string;
  active_count: number;
  deprecated_count: number;
  active: Category[];
  deprecated: Category[];
  last_sync: {
    synced_at: string;
    outcome: string;
    source_version: string | null;
    categories_after: number;
    triggered_by: string;
  } | null;
}

export default function CategorySyncManagement() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [history, setHistory] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<Record<string, any>>({});

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const [ovRes, histRes] = await Promise.all([
        authenticatedFetch('/api/admin/kompetanseheving-categories'),
        authenticatedFetch('/api/admin/kompetanseheving-categories/history?limit=30'),
      ]);
      if (!ovRes.ok) throw new Error(`Oversikt: ${ovRes.status}`);
      if (!histRes.ok) throw new Error(`Historikk: ${histRes.status}`);
      setOverview(await ovRes.json());
      const h = await histRes.json();
      setHistory(h.history || []);
    } catch (e: any) {
      setError(e.message || 'Kunne ikke laste kategori-oversikt');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleManualSync() {
    if (syncing) return;
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/kompetanseheving-categories/sync', {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Sync feilet');
      const r = data.report;
      const parts = [];
      if (r.added?.length) parts.push(`${r.added.length} nye`);
      if (r.renamed?.length) parts.push(`${r.renamed.length} omdøpte`);
      if (r.deprecated?.length) parts.push(`${r.deprecated.length} deprekerte`);
      if (r.reactivated?.length) parts.push(`${r.reactivated.length} reaktiverte`);
      setSyncMessage(parts.length > 0 ? `Oppdatert: ${parts.join(', ')}` : 'Ingen endringer siden sist sync');
      await load();
    } catch (e: any) {
      setError(e.message || 'Sync feilet');
    } finally {
      setSyncing(false);
    }
  }

  async function loadUsage(slug: string) {
    if (usageData[slug]) return;
    try {
      const res = await authenticatedFetch(`/api/admin/kompetanseheving-categories/${slug}/usage`);
      if (!res.ok) return;
      const body = await res.json();
      setUsageData(prev => ({ ...prev, [slug]: body }));
    } catch (_) { /* ignorer */ }
  }

  function toggleExpand(slug: string) {
    if (expandedSlug === slug) {
      setExpandedSlug(null);
    } else {
      setExpandedSlug(slug);
      loadUsage(slug);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500">Laster kategori-oversikt…</p>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">{error}</div>
      </div>
    );
  }

  const lastSyncOutcome = overview?.last_sync?.outcome;
  const lastSyncTime = overview?.last_sync?.synced_at;
  const lastSyncAgoMin = lastSyncTime
    ? Math.max(0, Math.floor((Date.now() - new Date(lastSyncTime).getTime()) / 60000))
    : null;

  return (
    <div className="space-y-6">
      {/* Header + sync-knapp */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Tag size={22} className="text-kf-blue-600" />
              Kategori-sync
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Daglig synkronisering av kanoniske hovedkategorier fra{' '}
              <a href={overview?.source_url} target="_blank" rel="noreferrer" className="text-kf-blue-600 hover:underline inline-flex items-center gap-1">
                kompetanseheving.no <ExternalLink size={12} />
              </a>
            </p>
          </div>
          <button
            onClick={handleManualSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Synkroniserer…' : 'Synkroniser nå'}
          </button>
        </div>

        {syncMessage && (
          <div className="mt-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded flex items-start gap-2">
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
            <span>{syncMessage}</span>
          </div>
        )}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-start gap-2">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Statuskort */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <StatusCard
            icon={<Tag size={20} />}
            label="Aktive kategorier"
            value={String(overview?.active_count ?? '—')}
            tone="blue"
          />
          <StatusCard
            icon={<AlertTriangle size={20} />}
            label="Deprekerte"
            value={String(overview?.deprecated_count ?? '—')}
            tone={overview?.deprecated_count ? 'warning' : 'muted'}
          />
          <StatusCard
            icon={<Clock size={20} />}
            label="Sist synkronisert"
            value={
              lastSyncAgoMin === null
                ? '—'
                : lastSyncAgoMin < 1
                  ? 'Akkurat nå'
                  : lastSyncAgoMin < 60
                    ? `${lastSyncAgoMin} min siden`
                    : lastSyncAgoMin < 1440
                      ? `${Math.floor(lastSyncAgoMin / 60)} t siden`
                      : `${Math.floor(lastSyncAgoMin / 1440)} dager siden`
            }
            tone={lastSyncOutcome === 'error' ? 'warning' : 'muted'}
            sublabel={overview?.last_sync?.triggered_by && `trigger: ${overview.last_sync.triggered_by}`}
          />
        </div>
      </div>

      {/* Deprekerte kategorier — høyere prioritet, vist først hvis finnes */}
      {overview && overview.deprecated.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <AlertTriangle size={20} className="text-amber-600" />
            Deprekerte kategorier ({overview.deprecated.length})
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Disse kategoriene finnes ikke lenger på kompetanseheving.no. Studieplaner som
            bruker dem fortsetter å fungere, men kan ikke lenger velges for nye kurs.
            Klikk for å se hvilke kursinstanser som er berørt.
          </p>
          <div className="space-y-2">
            {overview.deprecated.map(cat => (
              <DeprecatedCategoryRow
                key={cat.slug}
                category={cat}
                expanded={expandedSlug === cat.slug}
                usage={usageData[cat.slug]}
                onToggle={() => toggleExpand(cat.slug)}
                replacement={
                  cat.replacement_slug
                    ? overview.active.find(c => c.slug === cat.replacement_slug)?.name || null
                    : null
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Aktive kategorier */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <TrendingUp size={20} className="text-kf-blue-600" />
          Aktive kategorier ({overview?.active_count || 0})
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {overview?.active.map(cat => (
            <div key={cat.slug} className="border border-gray-200 rounded px-3 py-2 text-sm">
              <div className="font-medium text-gray-900">{cat.name}</div>
              <div className="text-xs text-gray-500 font-mono truncate" title={cat.slug}>{cat.slug}</div>
              {cat.aliases.length > 0 && (
                <div className="text-xs text-gray-400 mt-1 line-clamp-1" title={cat.aliases.join(', ')}>
                  {cat.aliases.length} alias
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Historikk */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <History size={20} className="text-gray-500" />
          Synkroniserings-historikk ({history.length})
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="py-2 pr-4">Tidspunkt</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Trigger</th>
                <th className="py-2 pr-4">Endringer</th>
                <th className="py-2 pr-4">Totalt</th>
                <th className="py-2">Varighet</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                    {new Date(h.synced_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}
                  </td>
                  <td className="py-2 pr-4"><OutcomeBadge outcome={h.outcome} /></td>
                  <td className="py-2 pr-4 text-xs text-gray-500 font-mono">{h.triggered_by}</td>
                  <td className="py-2 pr-4 text-xs">
                    {h.outcome === 'error' ? (
                      <span className="text-red-600" title={h.error_message || ''}>
                        {h.error_message?.slice(0, 60) || 'ukjent feil'}
                      </span>
                    ) : (
                      <ChangeSummary log={h} />
                    )}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{h.categories_after ?? '—'}</td>
                  <td className="py-2 text-gray-500 text-xs">{h.duration_ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon, label, value, tone, sublabel }: { icon: React.ReactNode; label: string; value: string; tone: 'blue' | 'warning' | 'muted'; sublabel?: string | false | null }) {
  const toneClasses = {
    blue: 'bg-kf-blue-50 border-kf-blue-200 text-kf-blue-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    muted: 'bg-gray-50 border-gray-200 text-gray-900',
  }[tone];
  return (
    <div className={`border rounded-lg p-4 ${toneClasses}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sublabel && <div className="text-xs opacity-60 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    success: { label: 'OK', cls: 'bg-green-100 text-green-800' },
    no_change: { label: 'Ingen endring', cls: 'bg-gray-100 text-gray-700' },
    partial: { label: 'Delvis', cls: 'bg-amber-100 text-amber-800' },
    error: { label: 'Feil', cls: 'bg-red-100 text-red-800' },
  };
  const o = map[outcome] || { label: outcome, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${o.cls}`}>{o.label}</span>;
}

function ChangeSummary({ log }: { log: SyncLog }) {
  const parts = [];
  if (log.added_slugs?.length) parts.push(`+${log.added_slugs.length} nye`);
  if (log.renamed?.length) parts.push(`${log.renamed.length} omdøpte`);
  if (log.deprecated_slugs?.length) parts.push(`-${log.deprecated_slugs.length} deprekerte`);
  if (log.reactivated_slugs?.length) parts.push(`${log.reactivated_slugs.length} reaktiverte`);
  if (parts.length === 0) return <span className="text-gray-400">—</span>;
  return <span className="text-gray-700">{parts.join(', ')}</span>;
}

function DeprecatedCategoryRow({
  category, expanded, usage, onToggle, replacement,
}: {
  category: Category;
  expanded: boolean;
  usage: any;
  onToggle: () => void;
  replacement: string | null;
}) {
  return (
    <div className="border border-amber-200 rounded">
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 hover:bg-amber-50 flex items-start justify-between gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900">{category.name}</div>
          <div className="text-xs text-gray-500 font-mono">{category.slug}</div>
          {replacement && (
            <div className="text-xs text-amber-800 mt-1">
              → erstattes av: <strong>{replacement}</strong>
            </div>
          )}
          {category.deprecated_at && (
            <div className="text-xs text-gray-500 mt-1">
              Deprekert: {new Date(category.deprecated_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}
            </div>
          )}
        </div>
        <span className="text-xs text-kf-blue-600 flex-shrink-0 mt-1">
          {expanded ? '▴ Skjul' : '▾ Vis bruk'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-amber-100 bg-amber-50/40 px-4 py-3 text-sm">
          {!usage && <div className="text-gray-500">Henter bruksoversikt…</div>}
          {usage && (
            <>
              {usage.course_instance_count === 0 ? (
                <div className="text-gray-600">
                  Ingen kursinstanser bruker denne kategorien — trygt å overse.
                </div>
              ) : (
                <>
                  <div className="font-medium text-gray-900 mb-2">
                    Kursinstanser som bruker kategorien ({usage.course_instance_count}):
                  </div>
                  <ul className="space-y-1">
                    {usage.course_instances.slice(0, 10).map((ci: any) => (
                      <li key={ci.id} className="text-xs text-gray-700">
                        <span className="font-medium">{ci.title}</span>
                        {' · '}
                        <span className="text-gray-500">{ci.member_name || 'ukjent arrangør'}</span>
                        {ci.visible_on_kompetanseheving && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">Synlig på kompetanseheving.no</span>
                        )}
                      </li>
                    ))}
                    {usage.course_instance_count > 10 && (
                      <li className="text-xs text-gray-500">… og {usage.course_instance_count - 10} til</li>
                    )}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
